import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack, Typography,
  Chip, Divider, TextField, MenuItem, CircularProgress, Alert, Link, IconButton,
  Tooltip, Paper,
} from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SendIcon from '@mui/icons-material/Send';
import {
  getContactInsights, generateLinkedInMessage, logSentLinkedInMessage, getAiProviders,
  getInteractionTemplates,
} from '../services/api';

const MESSAGE_TYPES = [
  { value: 'ConnectionRequest', label: 'Connection Request (≤200 chars)' },
  { value: 'FirstMessage', label: 'First Message' },
  { value: 'FollowUp', label: 'Follow-up' },
];

const TONES = ['Professional', 'Friendly', 'Direct', 'Casual'];

const STAGES = ['FirstContact', 'FollowUp', 'Qualification', 'ReEngagement', 'DemoInvite', 'Negotiation'];

export default function LinkedInContactDialog({ open, contactId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insights, setInsights] = useState(null);

  const [messageType, setMessageType] = useState('ConnectionRequest');
  const [tone, setTone] = useState('Professional');
  const [maxLength, setMaxLength] = useState(200);
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [context, setContext] = useState('');
  const [aiProviders, setAiProviders] = useState([]);
  const [aiProvider, setAiProvider] = useState('openai');
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [variants, setVariants] = useState([]);
  const [logging, setLogging] = useState(false);
  const [stage, setStage] = useState('FirstContact');
  const [logged, setLogged] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadInsights = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await getContactInsights(contactId);
      setInsights(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAiProviders();
        const list = (data || []).filter(p => p.isAvailable);
        setAiProviders(list);
        const openai = list.find(p => (p.name || '').toLowerCase() === 'openai');
        if (openai) setAiProvider(openai.name);
        else if (list.length > 0) setAiProvider(list[0].name);
      } catch { /* default openai */ }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getInteractionTemplates({ channel: 'LinkedIn' });
        const list = Array.isArray(data) ? data : (data?.items || []);
        setTemplates(list.filter(t => t.isActive !== false));
      } catch { /* ignore */ }
    })();
  }, []);

  const handleTemplateChange = (id) => {
    setTemplateId(id);
    if (!id) return;
    const tpl = templates.find(t => String(t.id) === String(id));
    if (!tpl) return;
    if (tpl.maxLength && Number(tpl.maxLength) > 0) setMaxLength(Number(tpl.maxLength));
    if (tpl.bodyTemplate) {
      setMessage(tpl.bodyTemplate);
      setVariants([]);
      setLogged(false);
    }
    if (tpl.tone) setTone(tpl.tone);
  };

  useEffect(() => {
    if (open && contactId) {
      setMessage('');
      setVariants([]);
      setLogged(false);
      setCopied(false);
      setContext('');
      setMessageType('ConnectionRequest');
      setMaxLength(200);
      setTemplateId('');
      setStage('FirstContact');
      loadInsights();
    }
  }, [open, contactId, loadInsights]);

  const charLimit = Number(maxLength) > 0 ? Number(maxLength) : null;
  const overLimit = charLimit && message.length > charLimit;

  const handleGenerate = async () => {
    if (!insights) return;
    setGenerating(true);
    setError('');
    try {
      const tpl = templateId ? templates.find(t => String(t.id) === String(templateId)) : null;
      const { data } = await generateLinkedInMessage({
        leadId: insights.leadId,
        contactId: insights.contactId,
        messageType,
        tone,
        maxLength: Number(maxLength) > 0 ? Number(maxLength) : 200,
        templateId: templateId ? Number(templateId) : undefined,
        templateBody: tpl?.bodyTemplate || undefined,
        context: context || undefined,
        aiProviderName: aiProvider || undefined,
      });
      setMessage(data?.message || '');
      setVariants(data?.variants || []);
      setLogged(false);
    } catch (e) {
      setError(e?.response?.data || 'Failed to generate message');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const handleLogSent = async () => {
    if (!insights || !message.trim()) return;
    setLogging(true);
    setError('');
    try {
      await logSentLinkedInMessage({
        leadId: insights.leadId,
        contactId: insights.contactId,
        messageType,
        stage,
        body: message,
        notes: context || undefined,
      });
      setLogged(true);
    } catch (e) {
      setError(e?.response?.data || 'Failed to log');
    } finally {
      setLogging(false);
    }
  };

  const openLinkedIn = () => {
    if (insights?.linkedInUrl) window.open(insights.linkedInUrl, '_blank', 'noopener');
  };

  const fullName = insights?.fullName || '(no name)';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <LinkedInIcon sx={{ color: '#0a66c2' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>{fullName}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {insights?.jobTitle ? `${insights.jobTitle} · ` : ''}{insights?.leadCompanyName}
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: { xs: 'auto', md: '74vh' }, overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{String(error)}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
        ) : !insights ? null : (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={0}
          alignItems="stretch"
          divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />}
          sx={{ height: '100%' }}
        >
          <Box sx={{ flex: 1.1, minWidth: 0, height: { xs: 'auto', md: '100%' }, overflowY: 'auto', p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>LinkedIn Helper</Typography>
            {!insights.linkedInUrl && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No LinkedIn URL stored. You can still generate a message, but no profile to open.
              </Alert>
            )}

            <TextField
              select
              size="small"
              label="Template"
              value={templateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              helperText={templateId
                ? 'Body and max length auto-filled from template'
                : 'Optional — pick a LinkedIn template to prefill body & max length'}
            >
              <MenuItem value="">(None — use AI from scratch)</MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}{t.maxLength ? ` · ${t.maxLength} chars` : ''}{t.stage ? ` · ${t.stage}` : ''}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
              <TextField
                select
                size="small"
                label="Message type"
                value={messageType}
                onChange={(e) => setMessageType(e.target.value)}
                fullWidth
              >
                {MESSAGE_TYPES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="Tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {TONES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField
                size="small"
                type="number"
                label="Max length"
                value={maxLength}
                onChange={(e) => setMaxLength(e.target.value)}
                inputProps={{ min: 50, max: 2000, step: 10 }}
                helperText="LinkedIn cap: 200 (connect), 8000 (msg)"
                sx={{ minWidth: 140 }}
              />
              <TextField
                select
                size="small"
                label="AI provider"
                value={aiProvider}
                onChange={(e) => setAiProvider(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {aiProviders.length === 0 && <MenuItem value="openai">OpenAI</MenuItem>}
                {aiProviders.map((p) => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.displayName || p.name}{p.isDefault ? ' (default)' : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Optional context / angle"
              placeholder="e.g. saw they just raised Series B; want to reference our analytics use case"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                onClick={handleGenerate}
                disabled={generating}
              >
                {generating ? 'Generating…' : message ? 'Regenerate' : 'Generate'}
              </Button>
              {insights.linkedInUrl && (
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  onClick={openLinkedIn}
                >
                  Open LinkedIn
                </Button>
              )}
            </Stack>

            <TextField
              size="small"
              fullWidth
              multiline
              minRows={6}
              label="Message"
              value={message}
              onChange={(e) => { setMessage(e.target.value); setLogged(false); }}
              error={!!overLimit}
              helperText={charLimit
                ? `${message.length} / ${charLimit} chars${overLimit ? ' — over LinkedIn limit' : ''}`
                : `${message.length} chars`}
            />

            {variants.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Variants
                </Typography>
                <Stack spacing={1}>
                  {variants.map((v, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => setMessage(v)}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{v}</Typography>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                select
                size="small"
                label="Stage"
                value={stage}
                onChange={(e) => setStage(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                {STAGES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <Tooltip title="Copy message to clipboard, paste into LinkedIn, then log it">
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    disabled={!message}
                    onClick={handleCopy}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained"
                color="success"
                startIcon={logging ? <CircularProgress size={16} /> : <SendIcon />}
                disabled={logging || !message.trim() || logged}
                onClick={handleLogSent}
              >
                {logged ? 'Logged ✓' : logging ? 'Logging…' : 'Mark sent & log'}
              </Button>
            </Stack>
            {logged && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Interaction logged. Lead moved to InOutreach if applicable.
              </Alert>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, height: { xs: 'auto', md: '100%' }, overflowY: 'auto', p: 2 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Insights</Typography>
            <InsightsTab insights={insights} openLinkedIn={openLinkedIn} />
          </Box>
        </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {insights && (
          <Button onClick={() => window.open(`/leads/${insights.leadId}`, '_blank')}>
            Open lead
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function InsightsTab({ insights, openLinkedIn }) {
  const Row = ({ label, value }) => value ? (
    <Stack direction="row" spacing={1} sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ minWidth: 110, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>{value}</Typography>
    </Stack>
  ) : null;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        {insights.isPrimary && <Chip label="Primary" size="small" color="warning" variant="outlined" />}
        {insights.isRecommendedForOutreach && <Chip label="Recommended" size="small" color="success" variant="outlined" />}
        {insights.seniorityLevel && insights.seniorityLevel !== 'Unknown' && (
          <Chip label={insights.seniorityLevel} size="small" color="primary" variant="outlined" />
        )}
        {insights.outreachScore > 0 && <Chip label={`Score ${insights.outreachScore}`} size="small" />}
        {insights.isUnsubscribed && <Chip label="Unsubscribed" size="small" color="error" />}
        {insights.linkedInUrl && (
          <Chip
            icon={<LinkedInIcon sx={{ color: '#0a66c2 !important' }} />}
            label="LinkedIn"
            size="small"
            onClick={openLinkedIn}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">Contact</Typography>
        <Row label="Title" value={insights.jobTitle ? `${insights.jobTitle}${insights.jobTitleConfirmed ? ' (confirmed)' : ''}` : null} />
        <Row label="Department" value={insights.department} />
        <Row label="Email" value={insights.email && <Link href={`mailto:${insights.email}`}>{insights.email}</Link>} />
        <Row label="Phone" value={insights.phone} />
        <Row label="LinkedIn" value={insights.linkedInUrl && (
          <Link href={insights.linkedInUrl} target="_blank" rel="noopener">
            {insights.linkedInUrl} <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
          </Link>
        )} />
        <Row label="Discovery" value={insights.discoverySource && `${insights.discoverySource}${insights.discoveryConfidence ? ` · ${insights.discoveryConfidence}%` : ''}`} />
        <Row label="Notes" value={insights.notes} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">Company</Typography>
        <Row label="Company" value={insights.leadCompanyName} />
        <Row label="Industry" value={insights.leadIndustryName} />
        <Row label="Country" value={insights.leadCountry} />
        <Row label="Status" value={insights.leadStatus} />
        <Row label="Website" value={insights.leadWebsite && (
          <Link href={insights.leadWebsite.startsWith('http') ? insights.leadWebsite : `https://${insights.leadWebsite}`} target="_blank" rel="noopener">
            {insights.leadWebsite}
          </Link>
        )} />
        <Row label="ICP fit" value={insights.aiIcpFit} />
      </Paper>

      {insights.leadAiInsights && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="overline" color="text.secondary">AI Insights</Typography>
            {insights.leadAiInsightsProvider && (
              <Chip label={insights.leadAiInsightsProvider} size="small" variant="outlined" />
            )}
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {insights.leadAiInsights}
          </Typography>
        </Paper>
      )}

      {insights.nextOutreachHint && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Typography variant="overline" color="text.secondary">Next outreach hint</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {insights.nextOutreachHint}
          </Typography>
        </Paper>
      )}

      {insights.recentInteractions?.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="overline" color="text.secondary">Recent interactions</Typography>
          <Stack divider={<Divider />} spacing={1} sx={{ mt: 1 }}>
            {insights.recentInteractions.map((i) => (
              <Box key={i.id}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Chip label={i.channel} size="small" variant="outlined" />
                  <Chip label={i.direction} size="small" color={i.direction === 'Inbound' ? 'success' : 'primary'} variant="outlined" />
                  <Chip label={i.status} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {new Date(i.interactionDate).toLocaleString()}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>{i.subject || '(no subject)'}</Typography>
                {i.aiSummary && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {i.aiSummary}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}
